import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { MarketplaceExtractAttributeGroup } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_marketplace_pb";

import {
  createMarketplaceExtractAttributeGroup,
  deleteMarketplaceExtractAttributeGroup,
  getMarketplaceExtractAttributeGroups,
} from "api/endpoints/marketplace";
import { gRPCStatus } from "api/grpc-status-codes";
import { callOptionsHeaders } from "api/helpers";
import AddAttributeGroupForm, {
  NewAttributeGroupFormState,
} from "components/marketplace/add-attributeGroup-form";
import { DEFAULT_REGION_CODE } from "constants/index";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

type ProductAttributeGroupRow = Pick<
  MarketplaceExtractAttributeGroup,
  "id" | "name" | "attributes"
>;

const defaultFormState: NewAttributeGroupFormState = {
  name: "",
  attributes: [],
};

const MarketplaceAttributeGroups = () => {
  const { showError } = useContext(SnackbarContext);
  const [openAddAttributeGroupDialog, setOpenAddAttributeGroupDialog] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [attributeGroupsData, setAttributeGroupsData] = useState<
    ProductAttributeGroupRow[]
  >([]);
  const [newAttributeGroupFormState, setNewAttributeGroupFormState] =
    useState(defaultFormState);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const attributeGroupsData = await getMarketplaceExtractAttributeGroups(
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );
      setIsLoading(false);
      setAttributeGroupsData(attributeGroupsData.attributeGroups);
    };
    init();
  }, []);

  const handleAddAttributeGroup = async () => {
    setIsLoading(true);
    try {
      const attributeGroupData = await createMarketplaceExtractAttributeGroup(
        {
          name: newAttributeGroupFormState.name,
          attributes: newAttributeGroupFormState.attributes,
        },
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );
      const attributeGroup = attributeGroupData.attributeGroup;

      if (!attributeGroup) {
        showError({
          code: gRPCStatus.UNKNOWN,
          name: "No attribute group returned",
          message:
            "Unknown error occurred adding a marketplace attribute group. Please try again later.",
        });
      } else {
        const fetchedAttributeGroupData =
          await getMarketplaceExtractAttributeGroups(
            callOptionsHeaders(DEFAULT_REGION_CODE),
          );
        setAttributeGroupsData(fetchedAttributeGroupData.attributeGroups);
      }
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setOpenAddAttributeGroupDialog(false);
      setNewAttributeGroupFormState(defaultFormState);
      setIsLoading(false);
    }
  };

  const handleDeleteAttributeGroup = async (attributeGroupId: string) => {
    try {
      await deleteMarketplaceExtractAttributeGroup(
        { attributeGroupId },
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );

      setAttributeGroupsData(
        attributeGroupsData.filter((group) => group.id !== attributeGroupId),
      );
    } catch (error) {
      showError(error as GrpcError);
    }
  };

  const renderAttributeGroupRecord = (row: ProductAttributeGroupRow) => (
    <TableRow key={`run-${row.id}`}>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        {row.name}
      </TableCell>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        {row.attributes.map((a) => a.name).join(", ")}
      </TableCell>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        <Button
          data-testid={`delete-${row.id}`}
          onClick={() => {
            handleDeleteAttributeGroup(row.id);
          }}
        >
          Delete
        </Button>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: "20px" }}>
          <Grid container alignContent="space-between" alignItems="center">
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h5" color="textPrimary" gutterBottom>
                  Extract Attribute Groups
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setOpenAddAttributeGroupDialog(true)}
                  data-testid="add-attributeGroup-dialog-btn"
                >
                  Add Attribute Group
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table aria-label="crowndin-templates">
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    <b>Name</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Attributes</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  attributeGroupsData.map((row) =>
                    renderAttributeGroupRecord(row),
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <AddAttributeGroupForm
        isOpen={openAddAttributeGroupDialog}
        handleClose={() => {
          setOpenAddAttributeGroupDialog(false);
        }}
        newAttributeGroupFormState={newAttributeGroupFormState}
        setNewAttributeGroupFormState={setNewAttributeGroupFormState}
        handleSubmit={() => {
          setIsLoading(true);
          handleAddAttributeGroup();
        }}
        isLoading={isLoading}
        isDisabled={
          !newAttributeGroupFormState.name ||
          !newAttributeGroupFormState.attributes
        }
      />
    </>
  );
};

export default MarketplaceAttributeGroups;
